import { useFlag } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { AlertWarningCircleIcon, LockIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useProductRefreshAppShell } from 'features/app_shell';
import { isRestrictedFeatureSelector } from 'features/billing';
import { useEventType } from 'features/event_type_provider';
import { groupsManagedByUserSelector, isAdminOrOwnerSelector } from 'selectors/current_user';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { appEditWorkflowUrl, appViewWorkflowUrl, getWorkflowsAppBasePath } from 'services/workflows/url';
import { AddAutomationMenu } from './add_automation_menu';
import { AutomationCard } from './automation_card';
import { AutomationsFreePlanBanner } from './automations_free_plan_banner';
import { getCrudAutomationIcon, getCrudAutomationSubtitle } from './crud_automation_card_copy';
import { WorkflowDeletionModal } from '../../notifications/advanced_notifications_section/workflow_deletion_modal';
import { isManagedWorkflow } from '../automation_workflow_utils';
import { LeaveWithoutPublishingPopup } from '../leave_without_publishing_popup';
import { useAvailableWorkflows } from '../use_available_workflows';
import { useLeaveWithoutPublishingConfirmation } from '../use_leave_without_publishing_confirmation';
import { useWorkflows } from '../use_workflows';
const sectionCss = "s18qf0fc";
const headerCss = "hbwhlpn";
const cardsCss = "c1mqtvqy";
const buttonCss = "b1kf4k1o";

// Deep links to the event type owner's Automations (workflows) app, where custom
// SMS/email notifications are built and reused across event types. Mirrors the
// legacy AdvancedNotificationSection owner-URL resolution.
const getAutomationsUrl = (eventType, isRebrandEnabled) => {
  const {
    profile,
    isShared,
    creator_id: creatorId,
    isMETParent
  } = eventType;
  if (isMETParent) return '/app/admin/managed_workflows';
  const owner = isShared ? {
    id: creatorId,
    type: 'user'
  } : {
    id: profile?.owner_id,
    type: profile?.owner_type?.toLowerCase().replace('page', 'team')
  };
  return `${getWorkflowsAppBasePath(isRebrandEnabled)}/${owner.type}/${owner.id}`;
};
const CreateAutomationButton = forwardRef((props, ref) => {
  const {
    url,
    onNavigationClick,
    ...restProps
  } = props;
  return <Button ref={ref} {...restProps} as={RouterLink} to={url} target="_blank" rel="noopener noreferrer" decoration="secondary-outline" className={classNames(buttonCss, restProps.className)} onClick={event => onNavigationClick(url, event)}>
      <UiIcon icon={IconExternalLink} size="small" title="" />
      {I18n.t('publisher.event_type_editor.sections.more_options.notifications.create_automation')}
    </Button>;
});
const HelpLink = ({
  children
}) => <Link href="https://calendly.com/help/how-to-create-a-managed-workflow" target="_blank" rel="noreferrer noopener" variant="subtle">
    {children}
  </Link>;
const canEditWorkflow = ({
  workflow,
  canManageWorkflows,
  isAdminOrOwner,
  managedGroupIds
}) => {
  const currentGroupAdmin = workflow.owner_type === 'user_group' && managedGroupIds.includes(workflow.owner_id);
  const personalWorkflow = !isManagedWorkflow(workflow);
  return canManageWorkflows && (personalWorkflow || currentGroupAdmin || isAdminOrOwner);
};
const getWorkflowOpenHandler = ({
  workflow,
  canEdit,
  isAdminOrOwner,
  isRebrandEnabled
}) => {
  const url = canEdit ? appEditWorkflowUrl({
    ownerId: workflow.owner_id,
    uuid: workflow.uuid,
    ownerType: workflow.owner_type
  }, isRebrandEnabled) : appViewWorkflowUrl({
    uuid: workflow.uuid,
    ownerId: workflow.owner_id,
    ownerType: workflow.owner_type,
    isAdminOrOwner,
    isRebrandEnabled
  });
  return () => window.open(url, '_blank');
};

// "Automations" section for the redesigned CRUD notifications tab. Renders the
// attached workflows as cards (per the v4 design) plus an entry point into the
// Automations app for creating new ones.
export const CrudAutomationsSection = () => {
  const eventType = useEventType();
  const workflows = useWorkflows({
    withAdminManaged: true
  });
  const availableWorkflows = useAvailableWorkflows({
    withAdminManaged: true
  });
  const isRebrandEnabled = useProductRefreshAppShell();
  const url = getAutomationsUrl(eventType, isRebrandEnabled);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const upgradeIsRequired = useSelector(state => isRestrictedFeatureSelector(state, {
    featureKey: 'workflows'
  }));
  const isLocked = isAnyLocksEnabled(eventType, ['v3_notifications']);
  const managedEventTypeEnabled = useFlag('raven_admin_managed_workflows_feature');
  const managedGroups = useSelector(groupsManagedByUserSelector);
  const managedGroupIds = managedGroups.map(({
    group_id: groupId
  }) => groupId);
  const {
    isMETParent
  } = eventType;
  const {
    list,
    canManageWorkflows
  } = workflows;
  const hasWorkflows = list && list.length > 0;
  const showWorkflows = !isMETParent || managedEventTypeEnabled || hasWorkflows;
  // "Add" only when the available-workflows catalog has loaded with at least one
  // entry. Without raven_workflows_extraction that query is skipped and always
  // empty, so fall back to the external "Create" link instead of an empty menu.
  const showAddAutomationMenu = availableWorkflows.length > 0;
  const [workflowForDeletion, setWorkflowForDeletion] = useState();
  const {
    isOpen: isLeaveConfirmationOpen,
    handleNavigationClick,
    handleDiscard,
    handleContinueEditing
  } = useLeaveWithoutPublishingConfirmation();
  const removeLabel = I18n.t('publisher.action.delete');
  if (!showWorkflows) return null;
  const closePopup = () => {
    setWorkflowForDeletion(null);
  };
  const renderAutomationAction = () => {
    if (upgradeIsRequired || !canManageWorkflows || isLocked) return null;
    if (showAddAutomationMenu) {
      return <AddAutomationMenu automationsUrl={url} availableWorkflows={availableWorkflows} onAutomationsNavigationClick={handleNavigationClick} />;
    }
    return <CreateAutomationButton url={url} onNavigationClick={handleNavigationClick} />;
  };
  return <div className={sectionCss} data-testid="crud-automations-section">
      <div className={headerCss}>
        <TitleText level={3} size="medium" as="h3">
          {I18n.t('publisher.event_type_editor.sections.more_options.notifications.automations_title')}
        </TitleText>
        <BodyText color="muted">
          {I18n.t('publisher.event_type_editor.sections.more_options.notifications.automations_helper')}
        </BodyText>
      </div>

      {upgradeIsRequired ? <AutomationsFreePlanBanner /> : <>
          {!canManageWorkflows && <ContextualBanner icon={LockIcon}>
              {I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.only_owner_can_make_changes')}
            </ContextualBanner>}

          {managedEventTypeEnabled && isMETParent && !isLocked && <ContextualBanner variant="subtle" icon={AlertWarningCircleIcon}>
              {HtmlParser(I18n.t('publisher.event_type_editor.sections.communications.advanced_notifications.assignees_can_add_workflows'), {
          HelpLink
        })}
            </ContextualBanner>}

          {hasWorkflows ? <div className={cardsCss}>
              {list.map(workflow => {
          const canEdit = canEditWorkflow({
            workflow,
            canManageWorkflows,
            isAdminOrOwner,
            managedGroupIds
          });
          return <AutomationCard key={workflow.uuid} data-testid={`automation-card-${workflow.uuid}`} icon={getCrudAutomationIcon(workflow)} title={workflow.name} subtitle={getCrudAutomationSubtitle(workflow, isRebrandEnabled)} onOpen={getWorkflowOpenHandler({
            workflow,
            canEdit,
            isAdminOrOwner,
            isRebrandEnabled
          })} onRemove={canEdit && !isLocked ? () => setWorkflowForDeletion(workflow.uuid) : undefined} showAdminManagedBadge={isManagedWorkflow(workflow)} removeLabel={removeLabel} />;
        })}
            </div> : null}

          {renderAutomationAction()}
        </>}

      {workflowForDeletion ? <WorkflowDeletionModal workflows={workflows} workflowUuid={workflowForDeletion} closePopup={closePopup} /> : null}

      <LeaveWithoutPublishingPopup isOpen={isLeaveConfirmationOpen} onClose={handleContinueEditing} onDiscard={handleDiscard} onContinueEditing={handleContinueEditing} />
    </div>;
};

require("./crud_automations_section.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./crud_automations_section.js");